import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { ArrowUpRight } from "lucide-react";
import { cn } from "../../lib/cn";

export function Suggestions({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-wrap gap-2", className)} {...props} />;
}

/** A one-click prompt, shown as a pill. */
export function Suggestion({ className, icon, children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { icon?: ReactNode }) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-full border border-sand-300 bg-white px-3 py-1.5 text-start text-xs text-aeblack-800 transition hover:-translate-y-px hover:border-aegold-300 hover:bg-aegold-50 hover:text-aegold-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:text-aegold-700",
        className,
      )}
      {...props}
    >
      {icon}
      <span className="truncate">{children}</span>
    </button>
  );
}

export function StarterCards({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("grid gap-2.5 sm:grid-cols-2", className)} {...props} />;
}

/** A way to begin, as a card: what it does in a line, and optional tags (the process it starts, how long it takes). */
export function StarterCard({ icon, title, description, meta, className, ...props }: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "title"> & {
  icon?: ReactNode; title: ReactNode; description?: ReactNode; meta?: ReactNode;
}) {
  return (
    <button
      type="button"
      className={cn(
        "group flex w-full items-start gap-3 rounded-xl border border-sand-200 bg-white p-3.5 text-start shadow-[0_1px_2px_rgb(43_38_32/0.05)] transition hover:-translate-y-px hover:border-aegold-300 hover:shadow-sand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-60",
        className,
      )}
      {...props}
    >
      {icon ? <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-aegold-50 text-aegold-700 ring-1 ring-aegold-100 transition group-hover:bg-aegold-100 [&_svg]:size-4.5">{icon}</span> : null}
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold leading-snug text-aeblack-950">{title}</span>
        {description ? <span className="mt-0.5 line-clamp-2 block text-xs leading-relaxed text-muted-foreground">{description}</span> : null}
        {meta ? <span className="mt-2 flex flex-wrap items-center gap-1.5">{meta}</span> : null}
      </span>
      <ArrowUpRight className="mt-0.5 size-4 shrink-0 text-sand-400 transition group-hover:text-aegold-700 rtl:-scale-x-100" />
    </button>
  );
}
